import request from 'sb-request'
import actions from 'redux/actions'
import { reducers, getState } from 'redux/core'
import { constants, getGqlQuery, modifyCosmeticsStructure } from 'common/helpers'
import { getFeatureToggle } from 'helpers'


/**
 * get all active types from types and categories filter
 *
 * @param {Array} types
 * @param {Array} categories
 * @param {String} root
 * @returns {Array}
 */
const getActiveTypes = (types = [], categories = [], root) => {
  if (!root || !categories.length) {
    return types
  }

  const allTypes = categories.reduce((result, categoryName) => {
    const typeMap = constants.cosmeticsProductTypes[root][categoryName]
    const types   = typeMap ? Object.values(typeMap) : []
    const isExist = types.some((type) => result.includes(type))

    // don't add all of category types if user selected one of them
    if (isExist) {
      return result
    }

    return [ ...result, ...types ]
  }, [ ...types ])

  return allTypes
}

/**
 * Fetches cosmetics products via grapql
 *
 * @param {Object} filters
 * @param {Object} params - request options
 * @returns {Promise<any>}
 */
const requestProductsGql = (filters = {}, params = {}) => {
  const { models: { products: { filters: { root }, activeFilters: { brands, categories, types } } } } = getState()

  const filter = {
    ...filters,
    brands: filters.brands || brands,
    types: filters.types || getActiveTypes(types, categories, root),
  }

  actions.models.products.setFetching(true)

  return request.post('graphql?name=products', {
    body: {
      query: `{
        ${getGqlQuery('products', filter)} {
          id
          url
          sex
          img
          type
          name
          brand
          rating
          labels
          category
          typeGroup
          upchargePrice
        }
      }`,
    },
    modifyResult: ({ products }) => products,
    ...params,
  })
    .then((items) => {
      const productsCount = items.length

      reducers.models.products.setProducts(items)
      reducers.models.products.setProductCount(productsCount)
    }, () => {
      reducers.models.products.setProducts([])
      reducers.models.products.setProductCount(0)
    })
}

const fetchSkincareProducts = ({ gender, productsFeed } = {}) => requestProductsGql({
  groups: [ constants.productTypeGroups.skincare ],
  productsFeed: productsFeed || constants.productsFeed.bodyWashes,
  gender,
})

const fetchProductsCareProducts = ({ gender, productsFeed } = {}) => requestProductsGql({
  groups: [ constants.productTypeGroups.personalCare ],
  productsFeed: productsFeed || constants.productsFeed.bodyWashes,
  gender,
})

const fetchMakeupProducts = () => requestProductsGql({
  groups: [ constants.productTypeGroups.makeup ],
})

const fetchProducts = ({ gender, productsFeed } = {}) => {
  const { models: { products: { filters: { root } } } } = getState()

  if (root === constants.cosmeticsCategories.skincare.name) {
    return fetchSkincareProducts({ gender, productsFeed })
  }

  if (root === constants.cosmeticsCategories.personalCare.name) {
    return fetchProductsCareProducts({ gender, productsFeed })
  }

  return fetchMakeupProducts()
}

/**
 * @typedef {Object} Catalogue
 * @property {number} id
 * @property {boolean} fetchLeaves
 */

/**
 * Fetches cosmetics products via grapql (new!)
 *
 * @param {Object} opts
 * @param {Catalogue[]} opts.catalogues
 * @param {string[]} opts.brands
 * @property {boolean} isGrouped
 * @returns {Promise<any>}
 */
const fetchProductsNew = ({ catalogues, activeBrandFilters = [], isGrouped }) => {
  const hasBrands = Boolean(activeBrandFilters.length)

  const productFields = `
    id
    url
    sex
    img
    type
    name
    brand
    rating
    labels
    category
    typeGroup
    upchargePrice
  `

  let query = catalogues.map(({ id, fetchLeaves }) => {
    if (fetchLeaves) {
      return `
        ${getGqlQuery(`catalogue_${id}: catalogue`, { id })} {
          ${getGqlQuery(`leaves`, { extra: !hasBrands })} {
            title: pageTitle
            ${getGqlQuery('products', { brands: activeBrandFilters })} {
              ${productFields}
            }
          }
        }
      `
    }

    return `
      ${getGqlQuery(`catalogue_${id}: catalogue`, { id })} {
        title: pageTitle
        ${getGqlQuery('products', { brands: activeBrandFilters })} {
          ${productFields}
        }
      }
    `
  })
    .join(' ')
  query = `{ ${query} }`

  actions.models.products.setFetching(true)

  return request.post('graphql?name=productsCatalogue', { body: { query } })
    .then((data) => {
      const catalogues = Object.values(data)

      if (isGrouped) {
        const groups = catalogues
          .reduce((result, { leaves, products, title }) => {
            if (leaves) {
              result = [ ...result, ...leaves ]
            }

            if (products) {
              result = [ ...result, { title, products } ]
            }

            return result
          }, [])
          .filter((group) => Boolean(group.products.length))

        const productCount = groups
          .reduce((result, group) => result + group.products.length, 0)

        return [ groups, productCount ]
      }

      const products = catalogues
        .map(({ leaves, products }) => {
          if (leaves) {
            return leaves
              .reduce((result, leaf) => [ ...result, ...leaf.products ], [])
          }

          if (products) {
            return products
          }

          return null
        })
        .filter((products) => Boolean(products))
        .reduce((result, products) => [ ...result, ...products ], [])

      return [ products, products.length ]
    })
    .then(([ products, productCount ]) => {
      reducers.models.products.setProducts(products)
      reducers.models.products.setProductCount(productCount)
    })
    .finally(() => actions.models.products.setFetching(false))
}

/**
 * Resets product to empty state
 *
 * @returns {void}
 */
const resetProducts = () => {
  reducers.models.products.setProducts([])
  reducers.models.products.setProductCount(0)
}

/**
 * Fetches brands for makeup products via rest
 *
 * @param {'male' | 'female'} gender
 * @param {*} opts
 * @returns {Promise<any>}
 */
const restFetchBrandsForMakeup = (gender, opts) => (
  request.get('rest/engine/makeup/brands', {
    query:{
      gender,
    },
    ...opts,
  })
)

/**
 * Fetches not empty types for cosmetics page
 *
 * @param [awaitFor] {Promise}
 */
const fetchNotEmptyTypes = (awaitFor) => {
  const { models: { me: { gender } } } = getState()

  const personalCareParams  = { kind: [ constants.productTypeGroups.personalCare ], gender }
  const skincareParams      = { kind: [ constants.productTypeGroups.skincare ], gender }
  const wellnessParams      = { kind: [ constants.productTypeGroups.wellness ] }
  const makeupParams        = { kind: [ constants.productTypeGroups.makeup ] }

  return request.post('graphql?name=fetchNotEmptyTypes', {
    withErrorModal: false,
    body: {
      query: `{
        skincare: ${getGqlQuery('productTypes', skincareParams)} {
          id
          name
          group
        }
        wellness: ${getGqlQuery('productTypes', wellnessParams)} {
          id
          name
          group
        }
        makeup: ${getGqlQuery('productTypes', makeupParams)} {
          id
          name
          group
        }
        personalCare: ${getGqlQuery('productTypes', personalCareParams)} {
          id
          name
          group
        }
      }`,
    },
  })
    .then((response) => {
      const { skincare = [], wellness = [], makeup = [], personalCare = [] } = (response || {})

      if (!awaitFor) {
        awaitFor = new Promise((resolve) => resolve())
      }

      awaitFor.then(() => {
        reducers.view.cosmeticsPage.setNotEmptyTypes({ skincare, wellness, makeup, personalCare })
      })
    })
}

/**
 * Fetches brands for cosmetics products via gql
 *
 * @param {'male' | 'female'} gender
 * @param {*} opts
 * @returns {Promise<any>}
 */
const gqlFetchBrands = (gender, opts) => {
  const { models: { products: { filters: { root }, activeFilters: { categories, types } } } } = getState()
  const filter = {
    types: getActiveTypes(types, categories, root),
    groups: constants.productTypeGroups[root],
  }

  if (root !== constants.cosmeticsCategories.makeup.name) {
    filter.gender = gender
  }

  return request.post('graphql?name=brands', {
    body: {
      query: `{
        ${getGqlQuery('brands', filter)} {
          name
          count
        }
      }`,
    },
    ...opts,
  })
}

/**
 * Fetches brands for cosmetics products
 *
 * @param {'male' | 'female'} gender
 * @returns {Promise<any>}
 */
const fetchBrands = (gender) => {
  const { models: { products: { filters: { root }, activeFilters: { brands: activeBrands } } } } = getState()
  const isNewCosmeticsBrandsFilterEnabled = getFeatureToggle(constants.features.newCosmeticsBrandsFilter)

  let action

  if (!isNewCosmeticsBrandsFilterEnabled && root === constants.cosmeticsCategories.makeup.name) {
    action = restFetchBrandsForMakeup
  }
  else {
    action = gqlFetchBrands
  }

  reducers.models.products.setFetchingBrands(true)

  return action(gender, {
    modifyResult: ({ brands }) => brands,
  })
    .then(
      (brands) => {
        const brandsForFilters = brands.map((item) => ({ ...item, label: item.name, value: item.name }))
        const nextBrandsValue  = brandsForFilters.map((item) => item.value)
        const brandsToRemove   = activeBrands.filter((brand) => nextBrandsValue.indexOf(brand) === -1)

        reducers.models.products.setFetchingBrands(false)
        brandsToRemove.forEach((value) => reducers.models.products.removeFilterValue({ filter: 'brands', value }))
        reducers.models.products.setBrands(brandsForFilters)

        return brands
      },
      () => {
        reducers.models.products.setFetchingBrands(false)
        reducers.models.products.setBrands([])
        return Promise.reject()
      }
    )
}

/**
 * Fetch Brands New
 * @param gqlQuery
 * @param activeBrandFilters {Array}
 * @returns {Promise<any | never>}
 */
const fetchBrandsNew = ({ gqlQuery, activeBrandFilters }) => {
  reducers.models.products.setFetchingBrands(true)

  return request.post('graphql?name=brands', {
    body: {
      query: `{
        ${getGqlQuery('brands', gqlQuery)} {
          name
          count
        }
      }`,
    },
    modifyResult: ({ brands }) => brands,
  })
    .then(
      (brands) => {
        const brandsForFilters = brands.map((item) => ({ ...item, label: item.name, value: item.name }))
        const nextBrandsValue  = brandsForFilters.map((item) => item.value)
        const brandsToRemove   = activeBrandFilters.filter((brand) => nextBrandsValue.indexOf(brand) === -1)

        reducers.models.products.setFetchingBrands(false)

        brandsToRemove.forEach((value) => reducers.models.products.removeFilterValue({ filter: 'brands', value }))

        reducers.models.products.setBrands(brandsForFilters)

        return brands
      },
      () => {
        reducers.models.products.setFetchingBrands(false)
        reducers.models.products.setBrands([])
        return Promise.reject()
      }
    )
}

/**
 * Resets brands
 * TODO: Delete it after brands will be added to all of cosmetics
 *
 * @returns {void}
 */
const resetBrands = () => {
  reducers.models.products.setFetchingBrands(true)
  reducers.models.products.setBrands([])
}

/**
 * Sets active filters on cosmetics page
 *
 * @param {string} root
 * @returns {void}
 */
const setFilters = (root) => {
  const categories = Object.values(constants.cosmeticsCategories[root].categories)
  const types = Object.values(constants.cosmeticsProductTypes[root])
    .reduce((result, sub) => [ ...result, ...(Object.values(sub) || []) ], [])

  reducers.models.products.setFilters({ filter: 'root', values: root })
  reducers.models.products.setFilters({ filter: 'categories', values: categories })
  reducers.models.products.setFilters({ filter: 'types', values: types })
}

const fetchCosmeticsDataStructure = () => {
  const dataFields = `
    id
    name
    pageTitle
    description
    url
    groupedProducts
    productsQuery {
      groups
      types
      gender
      productsFeed
    }
    seo {
      title
      description
    }
    analytics {
      page
      placement
    }
  `

  return request.post('graphql?name=cosmeticsData', {
    body: {
      query: `{
        ${getGqlQuery('rootCatalogue', {})} {
          children {
            name
            children {
              ${dataFields}
              children {
                ${dataFields}
                children {
                  ${dataFields}
                }
              }
            }
          }
        }
      }`,
    },
  })
    .then(modifyCosmeticsStructure)
    .then((data) => {
      reducers.view.cosmeticsPage.setStructure(data)

      return data
    })
}


export default {
  fetchBrands,
  fetchBrandsNew,
  resetBrands,
  fetchProducts,
  fetchProductsNew,
  resetProducts,
  fetchNotEmptyTypes,
  fetchSkincareProducts,
  fetchCosmeticsDataStructure,
  fetchMakeupProducts,
  setFilters,
}
